import { get, ref } from "firebase/database";
import { db } from "../lib/firebase";
import {
  ADS_ACTION_TYPES,
  BENEFIT_KEYS,
  PLAN_TYPE_CREDITS,
  PLAN_TYPE_PACKAGES,
} from "./constants";
import {
  PRICING_CREDIT_USAGE_CAMPAIGN,
  PRICING_CREDIT_USAGE_IMAGE,
  PRICING_CREDIT_USAGE_VIDEO,
  PRICING_CREDIT_USAGE_ANIMATE_IMAGE,
  PRICING_CREDIT_USAGE_ANALYZE_IMAGE_LAYERS,
  PRICING_CREDIT_USAGE_CUSTOM_REPORT,
  PRICING_CREDIT_USAGE_CUSTOM_REPORT_DECK,
  PRICING_CREDIT_USAGE_OPTIMIZATION,
  PRICING_CREDIT_USAGE_SCHEDULED_POST,
} from "../i18n/keysTranslations";

import { LaunchError } from "./errors";
import { store } from "../app/store";

/**
 * RTDB node under `agencies/${agencyID}/` holding optional per-agency credit costs.
 * Shape (partial overrides allowed): { campaign, image, video, animateImage, analyzeImageLayers, customReport, customReportDeck, optimization, scheduledPost }
 * All values are non-negative numbers; unknown keys are ignored.
 */
export const AGENCY_CREDIT_ACTION_COSTS_RTD_KEY = "creditActionCosts";

/**
 * RTDB node under `agencies/${agencyID}/` with string `"v1"` | `"v2"` (case-insensitive when read).
 */
export const AGENCY_PRICING_VERSION_RTD_KEY = "pricingVersion";

/** RTDB `agencies/{agencyID}/adSpendFeePercentage` — whole percent 1–100. */
export const AGENCY_AD_SPEND_FEE_PERCENTAGE_RTD_KEY = "adSpendFeePercentage";

/** Default when RTDB value is missing or invalid. */
export const DEFAULT_AD_SPEND_FEE_PERCENTAGE = 15;

export const CREDIT_PRICING_VERSION_V1 = "v1";
export const CREDIT_PRICING_VERSION_V2 = "v2";
export const CREDIT_PRICING_VERSION_V3 = "v3";
export const CREDIT_PRICING_VERSION_V4 = "v4";

export const CREDIT_PRICING_VERSIONS = Object.freeze({
  V1: CREDIT_PRICING_VERSION_V1,
  V2: CREDIT_PRICING_VERSION_V2,
  V3: CREDIT_PRICING_VERSION_V3,
  V4: CREDIT_PRICING_VERSION_V4,
});

/**
 * When `pricingVersion` is missing or invalid in RTDB, use this tier (aligns with {@link DEFAULT_CREDIT_ACTION_COSTS}).
 * Prefer setting `agencies/.../pricingVersion` for every agency so this is rarely used.
 */
export const DEFAULT_CREDIT_PRICING_VERSION = CREDIT_PRICING_VERSION_V4;

/** Flat fee once per new campaign (v1/v2). */
export const CAMPAIGN_BILLING_MODE_FLAT = "flat";
/** Fee per newly generated channel (v3). */
export const CAMPAIGN_BILLING_MODE_PER_CHANNEL = "perChannel";

const SNAPSHOT_KEYS = Object.freeze([
  "campaign",
  "image",
  "video",
  "animateImage",
  "analyzeImageLayers",
  "customReport",
  "customReportDeck",
  "optimization",
  "scheduledPost",
]);

const ACTION_DESCRIPTOR_OVERRIDES = Object.freeze({
  [CREDIT_PRICING_VERSION_V1]: Object.freeze({
    campaign: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "campaign",
      }),
    }),
    image: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "image",
      }),
    }),
    video: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "video",
      }),
    }),
    animateImage: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "animateImage",
      }),
    }),
    optimization: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "optimization",
      }),
    }),
    scheduledPost: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "scheduledPost",
      }),
    }),
    analyzeImageLayers: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "analyzeImageLayers",
      }),
    }),
    customReport: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "customReport",
      }),
    }),
  }),
  [CREDIT_PRICING_VERSION_V2]: Object.freeze({
    campaign: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "campaign",
      }),
    }),
    image: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "image",
      }),
    }),
    video: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "video",
      }),
    }),
    animateImage: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "animateImage",
      }),
    }),
    optimization: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "optimization",
      }),
    }),
    scheduledPost: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "scheduledPost",
      }),
    }),
    analyzeImageLayers: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "analyzeImageLayers",
      }),
    }),
    customReport: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "customReport",
      }),
    }),
  }),
  [CREDIT_PRICING_VERSION_V3]: Object.freeze({
    campaign: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "campaign",
      }),
    }),
    image: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "image",
      }),
    }),
    video: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: 0,
        videos: 1,
      }),
      activeMeterByPlanType: Object.freeze({
        [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
        [PLAN_TYPE_CREDITS]: ADS_ACTION_TYPES.VIDEOS,
      }),
      usageKeyByPlanType: Object.freeze({
        [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.VIDEOS,
        [PLAN_TYPE_CREDITS]: ADS_ACTION_TYPES.VIDEOS,
      }),
      ui: Object.freeze({
        showInCreditUsage: false,
      }),
    }),
    animateImage: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "animateImage",
      }),
    }),
    optimization: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "optimization",
      }),
    }),
    scheduledPost: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "scheduledPost",
      }),
      ui: Object.freeze({
        showInCreditUsage: false,
      }),
    }),
    analyzeImageLayers: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "analyzeImageLayers",
      }),
    }),
    customReport: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "customReport",
      }),
    }),
  }),
  [CREDIT_PRICING_VERSION_V4]: Object.freeze({
    campaign: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "campaign",
      }),
    }),
    image: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "image",
      }),
    }),
    video: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: 0,
        videos: 1,
      }),
      activeMeterByPlanType: Object.freeze({
        [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
        [PLAN_TYPE_CREDITS]: ADS_ACTION_TYPES.VIDEOS,
      }),
      usageKeyByPlanType: Object.freeze({
        [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.VIDEOS,
        [PLAN_TYPE_CREDITS]: ADS_ACTION_TYPES.VIDEOS,
      }),
      ui: Object.freeze({
        showInCreditUsage: false,
      }),
    }),
    animateImage: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "animateImage",
      }),
    }),
    optimization: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "optimization",
      }),
    }),
    scheduledPost: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "scheduledPost",
      }),
      ui: Object.freeze({
        showInCreditUsage: false,
      }),
    }),
    analyzeImageLayers: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "analyzeImageLayers",
      }),
    }),
    customReport: Object.freeze({
      costs: Object.freeze({
        [PLAN_TYPE_CREDITS]: "customReport",
      }),
    }),
  }),
});

const ACTION_DESCRIPTOR_BASES = Object.freeze({
  campaign: Object.freeze({
    id: "campaign",
    actionType: ADS_ACTION_TYPES.GENERATE_CAMPAIGN,
    translationKey: PRICING_CREDIT_USAGE_CAMPAIGN,
    costs: Object.freeze({
      [PLAN_TYPE_PACKAGES]: 1,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    }),
    activeMeterByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    usageKeyByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: "campaigns",
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    ui: Object.freeze({
      showInCreditUsage: true,
    }),
  }),
  image: Object.freeze({
    id: "image",
    actionType: ADS_ACTION_TYPES.CREATIVES,
    translationKey: PRICING_CREDIT_USAGE_IMAGE,
    costs: Object.freeze({
      [PLAN_TYPE_PACKAGES]: 1,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    }),
    activeMeterByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    usageKeyByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.CREATIVES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    ui: Object.freeze({
      showInCreditUsage: true,
    }),
  }),
  video: Object.freeze({
    id: "video",
    actionType: ADS_ACTION_TYPES.VIDEOS,
    translationKey: PRICING_CREDIT_USAGE_VIDEO,
    costs: Object.freeze({
      [PLAN_TYPE_PACKAGES]: 1,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    }),
    activeMeterByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    usageKeyByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.VIDEOS,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    ui: Object.freeze({
      showInCreditUsage: true,
    }),
  }),
  animateImage: Object.freeze({
    id: "animateImage",
    actionType: ADS_ACTION_TYPES.ANIMATE_IMAGE,
    translationKey: PRICING_CREDIT_USAGE_ANIMATE_IMAGE,
    costs: Object.freeze({
      [PLAN_TYPE_PACKAGES]: 1,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    }),
    activeMeterByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    usageKeyByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.VIDEOS,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    ui: Object.freeze({
      showInCreditUsage: true,
    }),
  }),
  optimization: Object.freeze({
    id: "optimization",
    actionType: ADS_ACTION_TYPES.MESSAGES,
    translationKey: PRICING_CREDIT_USAGE_OPTIMIZATION,
    costs: Object.freeze({
      [PLAN_TYPE_PACKAGES]: 1,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    }),
    activeMeterByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    usageKeyByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.MESSAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    ui: Object.freeze({
      showInCreditUsage: true,
    }),
  }),
  scheduledPost: Object.freeze({
    id: "scheduledPost",
    actionType: ADS_ACTION_TYPES.SCHEDULED_CONTENT,
    translationKey: PRICING_CREDIT_USAGE_SCHEDULED_POST,
    costs: Object.freeze({
      [PLAN_TYPE_PACKAGES]: 1,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    }),
    activeMeterByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    usageKeyByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.SCHEDULED_CONTENT,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    ui: Object.freeze({
      showInCreditUsage: true,
    }),
  }),
  analyzeImageLayers: Object.freeze({
    id: "analyzeImageLayers",
    actionType: ADS_ACTION_TYPES.ANALYZE_IMAGE_LAYERS,
    translationKey: PRICING_CREDIT_USAGE_ANALYZE_IMAGE_LAYERS,
    costs: Object.freeze({
      [PLAN_TYPE_PACKAGES]: 2,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    }),
    activeMeterByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    usageKeyByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.CREATIVES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    ui: Object.freeze({
      showInCreditUsage: true,
    }),
  }),
  customReport: Object.freeze({
    id: "customReport",
    actionType: ADS_ACTION_TYPES.GENERATE_CUSTOM_REPORT,
    translationKey: PRICING_CREDIT_USAGE_CUSTOM_REPORT,
    costs: Object.freeze({
      [PLAN_TYPE_PACKAGES]: 0,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    }),
    activeMeterByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    usageKeyByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.GENERATE_CUSTOM_REPORT,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    ui: Object.freeze({
      showInCreditUsage: true,
    }),
  }),
  // PPTX/PDF decks: same action type as CSV reports (usage key, BYOK, free
  // rules) but priced with `customReportDeck`. Declared after `customReport`
  // so the action-type index keeps the CSV descriptor.
  customReportDeck: Object.freeze({
    id: "customReportDeck",
    actionType: ADS_ACTION_TYPES.GENERATE_CUSTOM_REPORT,
    translationKey: PRICING_CREDIT_USAGE_CUSTOM_REPORT_DECK,
    costs: Object.freeze({
      [PLAN_TYPE_PACKAGES]: 0,
      [PLAN_TYPE_CREDITS]: "customReportDeck",
      videos: 0,
    }),
    activeMeterByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    usageKeyByPlanType: Object.freeze({
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.GENERATE_CUSTOM_REPORT,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    }),
    ui: Object.freeze({
      showInCreditUsage: true,
    }),
  }),
});

/**
 * @type {Readonly<Record<string, Readonly<{
 *   actionCosts: Readonly<Record<"campaign" | "image" | "video" | "animateImage" | "analyzeImageLayers" | "customReport" | "customReportDeck" | "optimization" | "scheduledPost", number>>;
 *   actionsWithCreditsCost: ReadonlyArray<"campaign" | "image" | "video" | "animateImage" | "analyzeImageLayers" | "customReport" | "customReportDeck" | "optimization" | "scheduledPost">;
 *   purchasePackages: ReadonlyArray<Readonly<{
 *     credits: number;
 *     price: number;
 *     pricePerCredit: number;
 *     popular?: boolean;
 *     bestValue?: boolean;
 *   }>>;
 *   appSumoPurchasePackages?: ReadonlyArray<Readonly<{
 *     credits: number;
 *     price: number;
 *     pricePerCredit: number;
 *     popular?: boolean;
 *     bestValue?: boolean;
 *   }>>;
 *   videoPurchasePackages: ReadonlyArray<Readonly<{
 *     videos: number;
 *     price: number;
 *     pricePerUnit: number;
 *   }>>;
 *   campaignBillingMode?: typeof CAMPAIGN_BILLING_MODE_FLAT | typeof CAMPAIGN_BILLING_MODE_PER_CHANNEL;
 * }>>>}
 */
const CREDIT_PRICING_PLAN_KEYS_V1_V3 = Object.freeze([
  "basic",
  "starter",
  "pro",
]);
const CREDIT_PRICING_PLAN_KEYS_V4 = Object.freeze([
  "starter",
  "studio",
  "agency",
]);

const GB_IN_BYTES = 1024 * 1024 * 1024;
export const STORAGE_TRIAL = 1 * GB_IN_BYTES;

export const PLANS_PAYMENT_LINKS_BY_VERSION = Object.freeze({
  [CREDIT_PRICING_VERSION_V1]: CREDIT_PRICING_PLAN_KEYS_V1_V3,
  [CREDIT_PRICING_VERSION_V2]: CREDIT_PRICING_PLAN_KEYS_V1_V3,
  [CREDIT_PRICING_VERSION_V3]: CREDIT_PRICING_PLAN_KEYS_V1_V3,
  [CREDIT_PRICING_VERSION_V4]: CREDIT_PRICING_PLAN_KEYS_V4,
});

const STORAGE_BY_PLANS_LEGACY = Object.freeze({
  basic: 15 * GB_IN_BYTES,
  growth: 50 * GB_IN_BYTES,
  starter: 50 * GB_IN_BYTES,
  pro: 150 * GB_IN_BYTES,
  advanced: 450 * GB_IN_BYTES,
});

const STORAGE_BY_PLANS_V4 = Object.freeze({
  starter: 15 * GB_IN_BYTES,
  studio: 50 * GB_IN_BYTES,
  agency: 150 * GB_IN_BYTES,
});

export const STORAGE_BY_PLANS_BY_VERSION = Object.freeze({
  [CREDIT_PRICING_VERSION_V1]: STORAGE_BY_PLANS_LEGACY,
  [CREDIT_PRICING_VERSION_V2]: STORAGE_BY_PLANS_LEGACY,
  [CREDIT_PRICING_VERSION_V3]: STORAGE_BY_PLANS_LEGACY,
  [CREDIT_PRICING_VERSION_V4]: STORAGE_BY_PLANS_V4,
});

export const STORAGE_BY_PLANS = Object.freeze({
  ...STORAGE_BY_PLANS_LEGACY,
  ...STORAGE_BY_PLANS_V4,
});

export const PLANS_PAYMENT_LINKS =
  PLANS_PAYMENT_LINKS_BY_VERSION[DEFAULT_CREDIT_PRICING_VERSION];

export const CREDIT_PRICING_BY_VERSION = Object.freeze({
  [CREDIT_PRICING_VERSION_V1]: Object.freeze({
    planKeys: CREDIT_PRICING_PLAN_KEYS_V1_V3,
    campaignBillingMode: CAMPAIGN_BILLING_MODE_FLAT,
    actionCosts: Object.freeze({
      campaign: 15,
      image: 1,
      video: 30,
      animateImage: 15,
      analyzeImageLayers: 5,
      customReport: 5,
      customReportDeck: 15,
      optimization: 1,
      scheduledPost: 1,
    }),
    actionsWithCreditsCost: Object.freeze([
      "campaign",
      "image",
      "video",
      "animateImage",
      "analyzeImageLayers",
      "customReport",
      "customReportDeck",
      "optimization",
      "scheduledPost",
    ]),
    purchasePackages: Object.freeze([
      Object.freeze({ credits: 50, price: 80, pricePerCredit: 1.6 }),
      Object.freeze({
        credits: 150,
        price: 240,
        pricePerCredit: 1.6,
        popular: true,
      }),
      Object.freeze({
        credits: 300,
        price: 480,
        pricePerCredit: 1.6,
        bestValue: true,
      }),
    ]),
    videoPurchasePackages: Object.freeze([]),
  }),
  [CREDIT_PRICING_VERSION_V2]: Object.freeze({
    planKeys: CREDIT_PRICING_PLAN_KEYS_V1_V3,
    campaignBillingMode: CAMPAIGN_BILLING_MODE_FLAT,
    actionCosts: Object.freeze({
      campaign: 35,
      image: 5,
      video: 35,
      animateImage: 15,
      analyzeImageLayers: 5,
      customReport: 5,
      customReportDeck: 15,
      optimization: 5,
      scheduledPost: 5,
    }),
    actionsWithCreditsCost: Object.freeze([
      "campaign",
      "image",
      "video",
      "animateImage",
      "analyzeImageLayers",
      "customReport",
      "customReportDeck",
      "optimization",
      "scheduledPost",
    ]),
    purchasePackages: Object.freeze([
      Object.freeze({ credits: 80, price: 96, pricePerCredit: 1.2 }),
      Object.freeze({
        credits: 200,
        price: 220,
        pricePerCredit: 1.1,
        popular: true,
      }),
      Object.freeze({
        credits: 400,
        price: 400,
        pricePerCredit: 1,
        bestValue: true,
      }),
    ]),
    videoPurchasePackages: Object.freeze([]),
  }),
  [CREDIT_PRICING_VERSION_V3]: Object.freeze({
    planKeys: CREDIT_PRICING_PLAN_KEYS_V1_V3,
    campaignBillingMode: CAMPAIGN_BILLING_MODE_PER_CHANNEL,
    actionCosts: Object.freeze({
      campaign: 5,
      image: 3,
      video: 0,
      animateImage: 15,
      analyzeImageLayers: 5,
      customReport: 5,
      customReportDeck: 15,
      optimization: 2,
      scheduledPost: 0,
    }),
    actionsWithCreditsCost: Object.freeze([
      "campaign",
      "image",
      "animateImage",
      "analyzeImageLayers",
      "customReport",
      "customReportDeck",
      "optimization",
    ]),
    purchasePackages: Object.freeze([
      Object.freeze({
        credits: 20,
        price: 49,
        pricePerCredit: 2.45,
      }),
      Object.freeze({
        credits: 50,
        price: 99,
        pricePerCredit: 1.98,
      }),
      Object.freeze({
        credits: 150,
        price: 258,
        pricePerCredit: 1.72,
        popular: true,
      }),
      Object.freeze({
        credits: 400,
        price: 636,
        pricePerCredit: 1.59,
        bestValue: true,
      }),
    ]),
    appSumoPurchasePackages: Object.freeze([
      Object.freeze({ credits: 65, price: 49, pricePerCredit: 0.75 }),
      Object.freeze({ credits: 150, price: 99, pricePerCredit: 0.66 }),
      Object.freeze({
        credits: 450,
        price: 257,
        pricePerCredit: 0.57,
        popular: true,
      }),
      Object.freeze({
        credits: 1200,
        price: 636,
        pricePerCredit: 0.53,
        bestValue: true,
      }),
    ]),
    videoPurchasePackages: Object.freeze([
      Object.freeze({ videos: 4, price: 75, pricePerUnit: 18.75 }),
      Object.freeze({ videos: 7, price: 126, pricePerUnit: 18 }),
      Object.freeze({ videos: 15, price: 255, pricePerUnit: 17 }),
    ]),
  }),
  [CREDIT_PRICING_VERSION_V4]: Object.freeze({
    planKeys: CREDIT_PRICING_PLAN_KEYS_V4,
    campaignBillingMode: CAMPAIGN_BILLING_MODE_PER_CHANNEL,
    actionCosts: Object.freeze({
      campaign: 5,
      image: 3,
      video: 0,
      animateImage: 15,
      analyzeImageLayers: 5,
      customReport: 5,
      customReportDeck: 15,
      optimization: 2,
      scheduledPost: 0,
    }),
    actionsWithCreditsCost: Object.freeze([
      "campaign",
      "image",
      "animateImage",
      "analyzeImageLayers",
      "customReport",
      "customReportDeck",
      "optimization",
    ]),
    purchasePackages: Object.freeze([
      Object.freeze({
        credits: 20,
        price: 49,
        pricePerCredit: 2.45,
      }),
      Object.freeze({
        credits: 50,
        price: 99,
        pricePerCredit: 1.98,
      }),
      Object.freeze({
        credits: 150,
        price: 258,
        pricePerCredit: 1.72,
        popular: true,
      }),
      Object.freeze({
        credits: 400,
        price: 636,
        pricePerCredit: 1.59,
        bestValue: true,
      }),
    ]),
    appSumoPurchasePackages: Object.freeze([
      Object.freeze({ credits: 65, price: 49, pricePerCredit: 0.75 }),
      Object.freeze({ credits: 150, price: 99, pricePerCredit: 0.66 }),
      Object.freeze({
        credits: 450,
        price: 257,
        pricePerCredit: 0.57,
        popular: true,
      }),
      Object.freeze({
        credits: 1200,
        price: 636,
        pricePerCredit: 0.53,
        bestValue: true,
      }),
    ]),
    videoPurchasePackages: Object.freeze([
      Object.freeze({ videos: 4, price: 75, pricePerUnit: 18.75 }),
      Object.freeze({ videos: 7, price: 126, pricePerUnit: 18 }),
      Object.freeze({ videos: 15, price: 255, pricePerUnit: 17 }),
    ]),
  }),
});

/** v1 tier costs (historical / legacy label). */
export const LEGACY_CREDIT_ACTION_COSTS = {
  ...CREDIT_PRICING_BY_VERSION[CREDIT_PRICING_VERSION_V1].actionCosts,
};

/** Latest tier: Redux fallbacks and “no agencyID” / RTDB error paths. */
export const DEFAULT_CREDIT_ACTION_COSTS = {
  ...CREDIT_PRICING_BY_VERSION[DEFAULT_CREDIT_PRICING_VERSION].actionCosts,
};

/**
 * @param {unknown} raw RTDB value for `pricingVersion`
 * @returns {typeof CREDIT_PRICING_VERSION_V1 | typeof CREDIT_PRICING_VERSION_V2 | typeof CREDIT_PRICING_VERSION_V3}
 */
export const normalizeAgencyPricingVersion = (raw) => {
  if (typeof raw !== "string") {
    return DEFAULT_CREDIT_PRICING_VERSION;
  }
  const key = raw.trim().toLowerCase();
  if (Object.prototype.hasOwnProperty.call(CREDIT_PRICING_BY_VERSION, key)) {
    return key;
  }
  return DEFAULT_CREDIT_PRICING_VERSION;
};

/**
 * @param {unknown} version
 * @returns {readonly string[]}
 */
export const getPlanKeysForVersion = (version) => {
  const key = normalizeAgencyPricingVersion(version);
  return (
    CREDIT_PRICING_BY_VERSION[key]?.planKeys ?? CREDIT_PRICING_PLAN_KEYS_V4
  );
};

/**
 * @param {unknown} pricingVersion
 * @returns {readonly string[]}
 */
export const getPlansPaymentLinksForVersion = (pricingVersion) => {
  const version = normalizeAgencyPricingVersion(pricingVersion);
  return (
    PLANS_PAYMENT_LINKS_BY_VERSION[version] ??
    PLANS_PAYMENT_LINKS_BY_VERSION[DEFAULT_CREDIT_PRICING_VERSION]
  );
};

/**
 * Entry (lowest) self-serve tier for a pricing version (first payment-link plan).
 * @param {unknown} pricingVersion
 * @returns {string | null}
 */
export const getEntryPlanForVersion = (pricingVersion) => {
  const plans = getPlansPaymentLinksForVersion(pricingVersion);
  return plans[0] ?? null;
};

/**
 * @param {string} plan
 * @param {unknown} [pricingVersion]
 * @returns {number}
 */
export const getStorageBytesForPlan = (plan, pricingVersion) => {
  const version = normalizeAgencyPricingVersion(pricingVersion);
  const map = STORAGE_BY_PLANS_BY_VERSION[version] ?? STORAGE_BY_PLANS_LEGACY;
  return map[plan] ?? STORAGE_BY_PLANS[plan] ?? STORAGE_TRIAL;
};

/** v3/v4 route AI video usage to the videos meter on credits plans. */
export const usesVideoMeterOnCreditsPlan = (version) => {
  const key = normalizeAgencyPricingVersion(version);
  return key === CREDIT_PRICING_VERSION_V3 || key === CREDIT_PRICING_VERSION_V4;
};

/**
 * Usage allocation keys assignable per child brand on credits plans.
 * @param {unknown} version
 * @returns {readonly string[]}
 */
export const resolveBrandUsageAllocationKeysForCreditsPlan = (version) => {
  const keys = [PLAN_TYPE_CREDITS];
  if (usesVideoMeterOnCreditsPlan(version)) {
    keys.push(ADS_ACTION_TYPES.VIDEOS);
  }
  return Object.freeze(keys);
};

/**
 * @param {unknown} usageKey
 * @param {unknown} version
 * @returns {boolean}
 */
export const isSupportedBrandUsageAllocationKey = (usageKey, version) =>
  resolveBrandUsageAllocationKeysForCreditsPlan(version).includes(usageKey);

/**
 * Remaining credits/videos to show for the active brand.
 * Without a brand cap, uses the agency pool. With a cap, uses the minimum of
 * agency pool remaining and brand allocation remaining.
 * @param {object} params
 * @param {number} params.agencyRemaining
 * @param {number | null | undefined} params.brandLimit
 * @param {number} [params.brandConsumed]
 * @returns {number}
 */
export const resolvePlanTextUsageRemaining = ({
  agencyRemaining,
  brandLimit,
  brandConsumed = 0,
}) => {
  const agencyPoolRemaining = Math.max(0, Number(agencyRemaining) || 0);

  const hasBrandCap =
    brandLimit !== undefined && brandLimit !== null && brandLimit !== "";

  if (!hasBrandCap) {
    return agencyPoolRemaining;
  }

  const limit = Math.max(0, Number(brandLimit) || 0);
  const consumed = Math.max(0, Number(brandConsumed) || 0);
  const brandRemaining = Math.max(0, limit - consumed);

  return Math.min(agencyPoolRemaining, brandRemaining);
};

/**
 * Display balance and brand-cap block state for usage gating (credits plans).
 * @param {object} params
 * @param {number} params.agencyRemaining Raw agency balance, before pending spend.
 * @param {Record<string, unknown>} [params.allocation]
 * @param {Record<string, unknown>} [params.consumed]
 * @param {string} params.usageKey
 * @param {unknown} params.creditPricingVersion
 * @param {number} [params.requiredSpend]
 * @param {number} [params.pendingSpend] In-flight spend not yet reflected in brand consumed.
 * @returns {{ displayBalance: number, isCapBlock: boolean }}
 */
export const resolveBrandUsageGate = ({
  agencyRemaining,
  allocation = {},
  consumed = {},
  usageKey,
  creditPricingVersion,
  requiredSpend = 0,
  pendingSpend = 0,
}) => {
  const normalizedPendingSpend =
    Number.isFinite(pendingSpend) && pendingSpend > 0 ? pendingSpend : 0;
  // Pending spend is charged once against each pool: the agency balance and
  // the brand cap. `agencyRemaining` is the raw balance.
  const agencyAvailable = Math.max(0, agencyRemaining - normalizedPendingSpend);

  if (!isSupportedBrandUsageAllocationKey(usageKey, creditPricingVersion)) {
    return {
      displayBalance: agencyAvailable,
      isCapBlock: false,
    };
  }

  const brandConsumed = Math.max(0, Number(consumed[usageKey]) || 0);
  const displayBalance = resolvePlanTextUsageRemaining({
    agencyRemaining: agencyAvailable,
    brandLimit: allocation[usageKey],
    brandConsumed: brandConsumed + normalizedPendingSpend,
  });

  const brandLimit = allocation[usageKey];
  const hasBrandCap =
    brandLimit !== undefined && brandLimit !== null && brandLimit !== "";

  let isCapBlock = false;
  if (requiredSpend > 0 && hasBrandCap) {
    const limit = Math.max(0, Number(brandLimit) || 0);
    const brandRemaining = Math.max(
      0,
      limit - brandConsumed - normalizedPendingSpend
    );
    isCapBlock = brandRemaining < requiredSpend;
  }

  return { displayBalance, isCapBlock };
};

const ACTION_ID_TO_CREDITS_PLAN_EXTRA_BENEFIT_KEY = Object.freeze({
  video: BENEFIT_KEYS.VIDEOS,
});

const ALL_CREDIT_COST_ACTION_IDS = Object.freeze([
  "campaign",
  "image",
  "video",
  "optimization",
  "scheduledPost",
]);

/**
 * @param {unknown} version Agency credit pricing version (`v1` / `v2` / `v3`), or unset before Redux hydrates.
 * @returns {readonly string[]}
 */
export const resolveActionsWithCreditsCostForVersion = (version) => {
  const key = normalizeAgencyPricingVersion(version);
  const actions = CREDIT_PRICING_BY_VERSION[key]?.actionsWithCreditsCost;
  return actions ? [...actions] : [...ALL_CREDIT_COST_ACTION_IDS];
};

/**
 * @param {unknown} version Agency credit pricing version (`v1` / `v2` / `v3`), or unset before Redux hydrates.
 * @returns {readonly string[]}
 */
export const resolveCreditsPlanExtraBenefitKeys = (version) => {
  const actionsWithCreditsCost = new Set(
    resolveActionsWithCreditsCostForVersion(version)
  );

  return ALL_CREDIT_COST_ACTION_IDS.reduce((extras, actionId) => {
    if (actionsWithCreditsCost.has(actionId)) {
      return extras;
    }

    const benefitKey = ACTION_ID_TO_CREDITS_PLAN_EXTRA_BENEFIT_KEY[actionId];
    if (benefitKey) {
      extras.push(benefitKey);
    }

    return extras;
  }, []);
};

/**
 * @param {unknown} version
 * @returns {Record<string, number>}
 */
export const resolveBaseCreditActionCostsForVersion = (version) => {
  const key = normalizeAgencyPricingVersion(version);
  return { ...CREDIT_PRICING_BY_VERSION[key].actionCosts };
};

/**
 * @param {unknown} version
 * @returns {typeof CAMPAIGN_BILLING_MODE_FLAT | typeof CAMPAIGN_BILLING_MODE_PER_CHANNEL}
 */
export const resolveCampaignBillingModeForVersion = (version) => {
  const key = normalizeAgencyPricingVersion(version);
  return (
    CREDIT_PRICING_BY_VERSION[key]?.campaignBillingMode ??
    CAMPAIGN_BILLING_MODE_FLAT
  );
};

/**
 * @param {Record<string, boolean> | undefined} generatedPlatforms
 * @returns {boolean}
 */
export const hasAnyGeneratedCampaignPlatform = (generatedPlatforms) =>
  Object.values(generatedPlatforms || {}).some(
    (generated) => generated === true
  );

/**
 * Usage multiplier for `generateCampaign` (credits plan).
 * @param {{ creditPricingVersion?: unknown, campaignExists?: boolean, platform?: string, generatedPlatforms?: Record<string, boolean> }} params
 * @returns {number}
 */
export const resolveCampaignUsageMultiplier = ({
  creditPricingVersion,
  campaignExists = false,
  platform,
  generatedPlatforms = {},
}) => {
  const mode = resolveCampaignBillingModeForVersion(creditPricingVersion);

  if (mode === CAMPAIGN_BILLING_MODE_PER_CHANNEL) {
    if (!platform) {
      return 1;
    }
    if (generatedPlatforms?.[platform]) {
      return 0;
    }
    return 1;
  }

  // v1/v2 flat: campaignID may exist from draft before the first channel is billed.
  if (hasAnyGeneratedCampaignPlatform(generatedPlatforms)) {
    return 0;
  }

  return campaignExists ? 0 : 1;
};

/**
 * @param {{ version: unknown; isAppSumo?: boolean }} params
 * @returns {Array<{ credits: number; price: number; pricePerCredit: number; popular?: boolean; bestValue?: boolean }>}
 */
export const resolveCreditPurchasePackagesForVersion = ({
  version,
  isAppSumo = false,
}) => {
  const key = normalizeAgencyPricingVersion(version);
  const tier = CREDIT_PRICING_BY_VERSION[key];
  const appSumoPackages = tier.appSumoPurchasePackages;
  const useAppSumo =
    isAppSumo === true &&
    Array.isArray(appSumoPackages) &&
    appSumoPackages.length > 0;
  const source = useAppSumo ? appSumoPackages : tier.purchasePackages;
  return source.map((p) => ({ ...p }));
};

/**
 * @param {unknown} version
 * @returns {Array<{ videos: number; price: number; pricePerUnit: number }>}
 */
export const resolveVideoPurchasePackagesForVersion = (version) => {
  const key = normalizeAgencyPricingVersion(version);
  return CREDIT_PRICING_BY_VERSION[key].videoPurchasePackages.map((p) => ({
    ...p,
  }));
};

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export const isValidAdSpendFeePercentage = (value) => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n >= 1 && n <= 100;
};

/**
 * Whole percent (e.g. 15 = 15%). Returns {@link DEFAULT_AD_SPEND_FEE_PERCENTAGE} when invalid.
 * @param {unknown} value
 * @returns {number}
 */
export const normalizeAdSpendFeePercentage = (value) => {
  if (isValidAdSpendFeePercentage(value)) {
    return Math.round(Number(value));
  }
  return DEFAULT_AD_SPEND_FEE_PERCENTAGE;
};

/** Latest tier bundles (fallback before Redux hydrates or when no agency). */
export const DEFAULT_CREDIT_PURCHASE_PACKAGES =
  resolveCreditPurchasePackagesForVersion({
    version: DEFAULT_CREDIT_PRICING_VERSION,
  });

/** V3 video bundles fallback when Redux has not hydrated `videoPurchasePackages`. */
export const DEFAULT_VIDEO_PURCHASE_PACKAGES =
  resolveVideoPurchasePackagesForVersion(DEFAULT_CREDIT_PRICING_VERSION);

/**
 * Reads `agencies/${agencyID}/pricingVersion` (normalized).
 * @param {string | undefined} agencyID
 * @returns {Promise<typeof CREDIT_PRICING_VERSION_V1 | typeof CREDIT_PRICING_VERSION_V2 | typeof CREDIT_PRICING_VERSION_V3>}
 */
export const fetchAgencyPricingVersion = async (agencyID) => {
  if (!agencyID) {
    return DEFAULT_CREDIT_PRICING_VERSION;
  }
  try {
    const snap = await get(
      ref(db, `agencies/${agencyID}/${AGENCY_PRICING_VERSION_RTD_KEY}`)
    );
    return normalizeAgencyPricingVersion(
      snap.exists() ? snap.val() : undefined
    );
  } catch (error) {
    store.dispatch(
      LaunchError(error, false, [], {
        functionName: "fetchAgencyPricingVersion",
        capture: "exception",
      })
    );
    console.log("Error in fetchAgencyPricingVersion", error);
    return DEFAULT_CREDIT_PRICING_VERSION;
  }
};

/**
 * Reads `agencies/${agencyID}/pricingVersion`, `creditActionCosts`, and `isAppSumo`; merges overrides.
 * When `isAppSumo` is true and the pricing tier defines {@link CREDIT_PRICING_BY_VERSION} `appSumoPurchasePackages` (v3), those bundles replace `purchasePackages` in the result.
 * @param {string | undefined} agencyID
 * @returns {Promise<{
 *   creditActionCosts: Record<string, number>;
 *   creditPurchasePackages: ReturnType<typeof resolveCreditPurchasePackagesForVersion>;
 *   videoPurchasePackages: ReturnType<typeof resolveVideoPurchasePackagesForVersion>;
 *   creditPricingVersion: typeof CREDIT_PRICING_VERSION_V1 | typeof CREDIT_PRICING_VERSION_V2 | typeof CREDIT_PRICING_VERSION_V3;
 *   adSpendFeePercentage: number;
 * }>}
 */
export const fetchMergedCreditPricingForAgency = async (agencyID) => {
  const fallbackVersion = DEFAULT_CREDIT_PRICING_VERSION;
  const fallbackPackages = resolveCreditPurchasePackagesForVersion({
    version: fallbackVersion,
  });
  if (!agencyID) {
    return {
      creditActionCosts: { ...DEFAULT_CREDIT_ACTION_COSTS },
      creditPurchasePackages: fallbackPackages,
      videoPurchasePackages:
        resolveVideoPurchasePackagesForVersion(fallbackVersion),
      creditPricingVersion: fallbackVersion,
      adSpendFeePercentage: DEFAULT_AD_SPEND_FEE_PERCENTAGE,
    };
  }
  try {
    const [versionSnap, agencySnap, appSumoSnap, adSpendFeeSnap] =
      await Promise.all([
        get(ref(db, `agencies/${agencyID}/${AGENCY_PRICING_VERSION_RTD_KEY}`)),
        get(
          ref(db, `agencies/${agencyID}/${AGENCY_CREDIT_ACTION_COSTS_RTD_KEY}`)
        ),
        get(ref(db, `agencies/${agencyID}/isAppSumo`)),
        get(
          ref(
            db,
            `agencies/${agencyID}/${AGENCY_AD_SPEND_FEE_PERCENTAGE_RTD_KEY}`
          )
        ),
      ]);
    const creditPricingVersion = normalizeAgencyPricingVersion(
      versionSnap.exists() ? versionSnap.val() : undefined
    );
    const isAppSumo = appSumoSnap.exists() && appSumoSnap.val() === true;
    const base = resolveBaseCreditActionCostsForVersion(creditPricingVersion);
    const raw = agencySnap.exists() ? agencySnap.val() : null;
    const creditActionCosts = mergeCreditActionCostOverrides(base, raw);
    const creditPurchasePackages = resolveCreditPurchasePackagesForVersion({
      version: creditPricingVersion,
      isAppSumo,
    });
    const videoPurchasePackages =
      resolveVideoPurchasePackagesForVersion(creditPricingVersion);
    const adSpendFeePercentage = normalizeAdSpendFeePercentage(
      adSpendFeeSnap.exists() ? adSpendFeeSnap.val() : undefined
    );
    return {
      creditActionCosts,
      creditPurchasePackages,
      videoPurchasePackages,
      creditPricingVersion,
      adSpendFeePercentage,
    };
  } catch (error) {
    store.dispatch(
      LaunchError(error, false, [], {
        functionName: "fetchMergedCreditPricingForAgency",
        capture: "exception",
      })
    );
    console.log("Error in fetchMergedCreditPricingForAgency", error);
    return {
      creditActionCosts: { ...DEFAULT_CREDIT_ACTION_COSTS },
      creditPurchasePackages: fallbackPackages,
      videoPurchasePackages:
        resolveVideoPurchasePackagesForVersion(fallbackVersion),
      creditPricingVersion: fallbackVersion,
      adSpendFeePercentage: DEFAULT_AD_SPEND_FEE_PERCENTAGE,
    };
  }
};

/**
 * @param {Record<string, number>} defaults
 * @param {unknown} raw
 * @returns {Record<string, number>}
 */
export const mergeCreditActionCostOverrides = (defaults, raw) => {
  if (!raw || typeof raw !== "object") {
    return { ...defaults };
  }
  const next = { ...defaults };
  for (const key of SNAPSHOT_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(raw, key)) continue;
    const n = Number(raw[key]);
    if (Number.isFinite(n) && n >= 0) {
      next[key] = n;
    }
  }
  return next;
};

/**
 * Safe snapshot for Redux gaps (logout/login) or missing persisted keys.
 * @param {unknown} snapshot
 * @returns {Record<string, number>}
 */
export const normalizeCreditActionCostsSnapshot = (snapshot) =>
  mergeCreditActionCostOverrides(DEFAULT_CREDIT_ACTION_COSTS, snapshot);

/**
 * @param {string | number} rawCost
 * @param {Record<string, number>} snapshot
 * @returns {number}
 */
const resolveActionCostValue = (rawCost, snapshot) => {
  if (typeof rawCost === "string") {
    return snapshot[rawCost] ?? 0;
  }

  return Number.isFinite(rawCost) ? rawCost : 0;
};

/**
 * @param {unknown} snapshot
 * @param {unknown} version
 */
export const buildActionDescriptors = (snapshot, version) => {
  const normalizedSnapshot = normalizeCreditActionCostsSnapshot(snapshot);
  const normalizedVersion = normalizeAgencyPricingVersion(version);
  const versionOverrides = ACTION_DESCRIPTOR_OVERRIDES[normalizedVersion] || {};

  return Object.values(ACTION_DESCRIPTOR_BASES).map((descriptor) => {
    const overrides = versionOverrides[descriptor.id] || {};
    const costsConfig = {
      ...descriptor.costs,
      ...(overrides.costs || {}),
    };

    const costs = Object.keys(costsConfig).reduce((acc, key) => {
      acc[key] = resolveActionCostValue(costsConfig[key], normalizedSnapshot);
      return acc;
    }, {});

    return {
      ...descriptor,
      costs,
      activeMeterByPlanType: {
        ...descriptor.activeMeterByPlanType,
        ...(overrides.activeMeterByPlanType || {}),
      },
      usageKeyByPlanType: {
        ...descriptor.usageKeyByPlanType,
        ...(overrides.usageKeyByPlanType || {}),
      },
      ui: {
        ...descriptor.ui,
        ...(overrides.ui || {}),
      },
    };
  });
};

/**
 * @param {ReadonlyArray<{id: string, actionType: string, costs: Record<string, number>, translationKey?: string, ui?: {showInCreditUsage?: boolean}}>} descriptors
 * @param {unknown} version
 */
const selectCreditCostsItems = (descriptors, version) =>
  descriptors
    .filter((descriptor) => descriptor.ui?.showInCreditUsage)
    .filter((descriptor) =>
      resolveActionsWithCreditsCostForVersion(version).includes(descriptor.id)
    )
    .map((descriptor) => ({
      id: descriptor.id,
      actionType: descriptor.actionType,
      credits: descriptor.costs[PLAN_TYPE_CREDITS] ?? 0,
      translationKey: descriptor.translationKey,
    }));

/**
 * First descriptor wins per action type. `customReport` is declared before
 * `customReportDeck`, so GENERATE_CUSTOM_REPORT stays the CSV price.
 * @param {ReadonlyArray<{actionType: string}>} descriptors
 */
const indexAdsCampaignActionCostsByType = (descriptors) => {
  const actionCostsByType = {};
  for (const descriptor of descriptors) {
    if (actionCostsByType[descriptor.actionType]) continue;
    actionCostsByType[descriptor.actionType] = descriptor;
  }

  actionCostsByType[ADS_ACTION_TYPES.GENERATE_AD_TEXT] = {
    actionType: ADS_ACTION_TYPES.GENERATE_AD_TEXT,
    costs: {
      [PLAN_TYPE_PACKAGES]: 1,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    },
    activeMeterByPlanType: {
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    },
    usageKeyByPlanType: {
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.GENERATE_AD_TEXT,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    },
    ui: {
      showInCreditUsage: false,
    },
  };

  actionCostsByType[ADS_ACTION_TYPES.GENERATE_TEXT] = {
    actionType: ADS_ACTION_TYPES.GENERATE_TEXT,
    costs: {
      [PLAN_TYPE_PACKAGES]: 0,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    },
    activeMeterByPlanType: {
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    },
    usageKeyByPlanType: {
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.GENERATE_TEXT,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    },
    ui: {
      showInCreditUsage: false,
    },
  };

  actionCostsByType[ADS_ACTION_TYPES.ADD_CONTENT_GALLERY] = {
    actionType: ADS_ACTION_TYPES.ADD_CONTENT_GALLERY,
    costs: {
      [PLAN_TYPE_PACKAGES]: 0,
      [PLAN_TYPE_CREDITS]: 0,
      videos: 0,
    },
    activeMeterByPlanType: {
      [PLAN_TYPE_PACKAGES]: PLAN_TYPE_PACKAGES,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    },
    usageKeyByPlanType: {
      [PLAN_TYPE_PACKAGES]: ADS_ACTION_TYPES.ADD_CONTENT_GALLERY,
      [PLAN_TYPE_CREDITS]: PLAN_TYPE_CREDITS,
    },
    ui: {
      showInCreditUsage: false,
    },
  };

  return actionCostsByType;
};

/**
 * @param {unknown} snapshot
 * @param {unknown} version
 */
export const buildCreditCostsItems = (snapshot, version) =>
  selectCreditCostsItems(buildActionDescriptors(snapshot, version), version);

/**
 * @param {unknown} snapshot
 * @param {unknown} version
 */
export const buildAdsCampaignActionCostsByType = (snapshot, version) =>
  indexAdsCampaignActionCostsByType(buildActionDescriptors(snapshot, version));

/**
 * @param {unknown} actionConfig
 * @param {string} planType
 */
export const resolveActionCostForPlanType = (actionConfig, planType) => {
  if (!actionConfig) {
    return {
      meterKey: planType,
      amount: 0,
      usageKey: planType,
    };
  }

  const meterKey =
    actionConfig.activeMeterByPlanType?.[planType] ||
    planType ||
    PLAN_TYPE_CREDITS;

  return {
    meterKey,
    amount: actionConfig.costs?.[meterKey] ?? 0,
    usageKey: actionConfig.usageKeyByPlanType?.[planType] || meterKey,
  };
};

/**
 * Whether an action log should count toward credits usage in the detailed modal.
 * @param {Record<string, unknown>} log
 * @param {unknown} actionConfig
 * @param {string} planType
 */
export const shouldIncludeActionLogInCreditsUsage = (
  log,
  actionConfig,
  planType
) => {
  if (planType !== PLAN_TYPE_CREDITS) {
    return true;
  }

  if (typeof log.meterKey === "string") {
    return log.meterKey === PLAN_TYPE_CREDITS;
  }

  if (typeof log.usageKey === "string") {
    return log.usageKey === PLAN_TYPE_CREDITS;
  }

  return (
    resolveActionCostForPlanType(actionConfig, planType).meterKey ===
    PLAN_TYPE_CREDITS
  );
};

/**
 * Credits spent for one action log. Prefers the cost recorded at charge time.
 * @param {Record<string, unknown>} log
 * @param {unknown} actionConfig
 * @param {string} planType
 */
export const resolveActionLogCreditsSpent = (log, actionConfig, planType) => {
  const multiplier = log.multiplier || 1;

  if (Number.isFinite(log.totalCost) && log.totalCost > 0) {
    return log.totalCost;
  }

  if (Number.isFinite(log.unitCost) && log.unitCost > 0) {
    return log.unitCost * multiplier;
  }

  const resolvedActionCost = resolveActionCostForPlanType(
    actionConfig,
    planType
  );
  const costToApply = actionConfig ? resolvedActionCost.amount : 1;
  return multiplier * costToApply;
};

/**
 * @param {unknown} snapshot
 * @param {unknown} version
 * @param {unknown} [adSpendFeePercentage]
 */
export const buildCreditCostsDerived = (
  snapshot,
  version,
  adSpendFeePercentage = DEFAULT_AD_SPEND_FEE_PERCENTAGE
) => {
  const descriptors = buildActionDescriptors(snapshot, version);
  const ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE =
    indexAdsCampaignActionCostsByType(descriptors);
  const customReportDeckDescriptor = descriptors.find(
    (descriptor) => descriptor.id === "customReportDeck"
  );

  return {
    adSpendFeePercentage: normalizeAdSpendFeePercentage(adSpendFeePercentage),
    creditCostsItems: selectCreditCostsItems(descriptors, version),
    ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE,
    ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION: {
      type: ADS_ACTION_TYPES.GENERATE_CAMPAIGN,
      costs:
        ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.GENERATE_CAMPAIGN]
          .costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.GENERATE_CAMPAIGN],
    },
    ADS_CAMPAIGN_GENERATE_AD_TEXT_ACTION: {
      type: ADS_ACTION_TYPES.GENERATE_AD_TEXT,
      costs:
        ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.GENERATE_AD_TEXT]
          .costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.GENERATE_AD_TEXT],
    },
    ADS_CAMPAIGN_GENERATE_TEXT_ACTION: {
      type: ADS_ACTION_TYPES.GENERATE_TEXT,
      costs:
        ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.GENERATE_TEXT].costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.GENERATE_TEXT],
    },
    ADS_CAMPAIGN_CHAT_BOT_ACTION: {
      type: ADS_ACTION_TYPES.MESSAGES,
      costs: ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.MESSAGES].costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.MESSAGES],
    },
    ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION: {
      type: ADS_ACTION_TYPES.CREATIVES,
      costs:
        ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.CREATIVES].costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.CREATIVES],
    },
    ADS_CAMPAIGN_GENERATE_SCHEDULED_CONTENT_ACTION: {
      type: ADS_ACTION_TYPES.SCHEDULED_CONTENT,
      costs:
        ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.SCHEDULED_CONTENT]
          .costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.SCHEDULED_CONTENT],
    },
    ADS_CAMPAIGN_GENERATE_VIDEO_ACTION: {
      type: ADS_ACTION_TYPES.VIDEOS,
      costs: ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.VIDEOS].costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.VIDEOS],
    },
    ADS_CAMPAIGN_ANIMATE_IMAGE_ACTION: {
      type: ADS_ACTION_TYPES.ANIMATE_IMAGE,
      costs:
        ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.ANIMATE_IMAGE].costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.ANIMATE_IMAGE],
    },
    ADS_CAMPAIGN_ANALYZE_IMAGE_LAYERS_ACTION: {
      type: ADS_ACTION_TYPES.ANALYZE_IMAGE_LAYERS,
      costs:
        ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[ADS_ACTION_TYPES.ANALYZE_IMAGE_LAYERS]
          .costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[
        ADS_ACTION_TYPES.ANALYZE_IMAGE_LAYERS
      ],
    },
    ADS_CAMPAIGN_GENERATE_CUSTOM_REPORT_ACTION: {
      type: ADS_ACTION_TYPES.GENERATE_CUSTOM_REPORT,
      costs:
        ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[
          ADS_ACTION_TYPES.GENERATE_CUSTOM_REPORT
        ].costs,
      ...ADS_CAMPAIGN_ACTION_COSTS_BY_TYPE[
        ADS_ACTION_TYPES.GENERATE_CUSTOM_REPORT
      ],
    },
    ADS_CAMPAIGN_GENERATE_CUSTOM_REPORT_DECK_ACTION: {
      type: ADS_ACTION_TYPES.GENERATE_CUSTOM_REPORT,
      costs: customReportDeckDescriptor.costs,
      ...customReportDeckDescriptor,
    },
  };
};
